import React, { useCallback, useEffect, useMemo, useState } from 'react'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { isBedbankOffer, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMaxChildAge,
  getTourV2OfferMinAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  isSalesforceTourV2,
} from 'lib/offer/offerUtils'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import OccupancyPickerVertical from 'components/Common/OccupancyPicker/OccupancyPickerVertical'
import noop from 'lib/function/noop'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer;
  open?: boolean;
  capacities?: Array<App.RoomCapacity>;
  childrenAgeLabel?: string
  className?: string;
  disableMultiRoom?: boolean;
  onChange?: (occupants: Array<App.Occupants | App.RoomOccupants>) => void;
}

const defaultRooms = [generateRoomOccupants()]

function OfferCapacityWithAgesVerticalSelection(props: Props) {
  const {
    rooms = defaultRooms,
    offer,
    capacities,
    childrenAgeLabel = 'Age at time of check-in',
    className,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
    onChange = noop,
  } = props

  const [localOccupancies, setLocalOccupancies] = useState<Array<App.Occupants | App.RoomOccupants>>(rooms)

  useEffect(() => {
    setLocalOccupancies(rooms)
  }, [rooms])

  const onOccupancyChange = useCallback((rooms: Array<App.Occupants | App.RoomOccupants>) => {
    setLocalOccupancies(rooms)
    onChange(rooms)
  }, [onChange])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbankOffer(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      if (isSalesforceTourV2(offer)) {
        return getTourV2OfferMinAge(offer)
      }
      return getTourV2OfferMaxChildAge(offer)
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && isSalesforceTourV2(offer)), [offer])

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  return (
    <OccupancyPickerVertical
      occupancies={localOccupancies}
      saleUnit={offer.saleUnit}
      maxChildAge={childrenAgeThreshold}
      maxInfantAge={infantAgeThreshold}
      capacities={capacities}
      data-testid="offer-capacity-with-ages-vertical-selection-dropdown"
      className={className}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      showChildren={!isLETour}
      onChange={onOccupancyChange}
      offerType={offer.type}
    />
  )
}

export default withOfferPageState((state) => ({
  rooms: state.rooms,
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
  open: state.activeFilter === 'capacity',
}), OfferCapacityWithAgesVerticalSelection, preventRenderWithClosedFilter)
